import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { bugsAPI, projectsAPI, tasksAPI, usersAPI } from '../utils/api';
import { Badge, ConfirmDialog, Modal } from '../components/layout/UI';
import { format, parseISO, isValid } from 'date-fns';
import BugFormFields, { bugToForm, bugFormValid } from '../components/BugFormFields';
import CustomFieldsEditor from '../components/CustomFieldsEditor';
import { useAuth } from '../hooks/useAuth';
import { isAdminRole } from '../utils/rolesConfig';

import { useToast, apiError } from '../components/Toast';
import { ArrowLeft, Paperclip, SquarePen, Trash2 } from 'lucide-react';

const EditIc = () => <SquarePen size={14} />;
const TrashIc = () => <Trash2 size={14} />;
const ClipIc = () => <Paperclip size={14} />;

const ENV_STYLE = { Production:'border border-red-500 text-red-600 dark:text-red-400', UAT:'border border-amber-500 text-amber-600 dark:text-amber-400', Development:'border border-blue-500 text-blue-600 dark:text-blue-400' };
// All ten values from bugs_status_check — a bug already in one of the
// missing statuses rendered an empty <select>.

const fmt = (val, pattern = 'MMM d, yyyy HH:mm') => {
  if (!val) return null;
  try { const d = typeof val === 'string' ? parseISO(val) : new Date(val); return isValid(d) ? format(d, pattern) : null; }
  catch { return null; }
};
const InfoRow = ({ label, value, children }) => (
  value || children ? (
    <div className="flex gap-3 py-2.5 border-b" style={{ borderColor:'var(--border)' }}>
      <p className="text-xs font-semibold w-32 flex-shrink-0 pt-0.5 uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{label}</p>
      <div className="flex-1 text-sm" style={{ color:'var(--text)' }}>{children || value}</div>
    </div>
  ) : null
);

export default function BugDetailPage() {
  const toast = useToast();
  const { id } = useParams();
  const navigate = useNavigate();
  const [bug,        setBug]        = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const { user: me } = useAuth();
  const [projects,   setProjects]   = useState([]);
  const [tasksList,  setTasksList]  = useState([]);
  const [usersList,  setUsersList]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen,   setEditOpen]   = useState(false);
  const [editForm,   setEditForm]   = useState({});
  const [saving,     setSaving]     = useState(false);

  const f = v => setEditForm(prev => ({ ...prev, ...v }));

  useEffect(() => {
    Promise.all([bugsAPI.getById(id), projectsAPI.getAll({ limit: 0 }), usersAPI.getAll()])
      .then(([bR, pR, uR]) => {
        setBug(bR.data); setProjects(pR.data); setUsersList(uR.data);
        if (bR.data?.project_id) tasksAPI.getAll({ project_id: bR.data.project_id , limit: 0 }).then(r => setTasksList(r.data)).catch(()=>{});
      }).catch(e => toast.error(apiError(e, 'Could not load this page.'))).finally(() => setLoading(false));
  }, [id]);

  const openEdit = () => {
    setEditForm(bugToForm(bug));
    setEditOpen(true);
  };
  const handleSave = async () => {
    setSaving(true);
    try { await bugsAPI.update(id, editForm);
    toast.success('Bug updated'); const r = await bugsAPI.getById(id); setBug(r.data); setEditOpen(false); }
    catch(e) { toast.error(apiError(e, 'Failed to save bug.')); } finally { setSaving(false); }
  };
  const handleDelete = async () => {
    try { await bugsAPI.delete(id); toast.success('Bug deleted'); navigate('/bugs'); }
    catch(e) { toast.error(apiError(e, 'Could not delete that bug.')); }
  };


  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!bug) return <div className="card text-center py-16"><p style={{ color:'var(--text)' }}>Bug not found</p><button onClick={() => navigate('/bugs')} className="btn-secondary mt-3">← Back</button></div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 text-sm flex-wrap" style={{ color:'var(--text-secondary)' }}>
        <button onClick={() => navigate('/bugs')} className="hover:text-brand-500 flex items-center gap-1"><ArrowLeft size={16}/> Bug Tracker</button>
        {bug.project_name && <><span>/</span><span>{bug.project_name}</span></>}
        <span>/</span><span style={{ color:'var(--text)' }}>{bug.title}</span>
      </div>

      <div className="card">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div className="flex-1">
            <h1 className="text-2xl font-bold mb-2" style={{ color:'var(--text)' }}>{bug.title}</h1>
            <div className="flex flex-wrap gap-2">
              <Badge value={bug.severity} type="severity" />
              <Badge value={bug.status}   type="status"   />
              {bug.environment && <span className={`badge ${ENV_STYLE[bug.environment]||''}`}>{bug.environment}</span>}
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={openEdit} className="btn-secondary flex items-center gap-1.5 text-sm"><EditIc /> Edit</button>
            {(bug?.created_by === me?.id || isAdminRole(me?.role)) && (
              <button onClick={() => setDeleteOpen(true)} className="btn-danger flex items-center gap-1.5 text-sm"><TrashIc /> Delete</button>
            )}
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b flex gap-1 overflow-x-auto" style={{ borderColor:'var(--border)' }}>
        {[
          { key:'overview',    label:'Overview'    },
          { key:'pipeline',    label:'QA Pipeline' },
          { key:'attachments', label:'Attachments' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors
              ${activeTab === tab.key ? 'border-brand-500 text-brand-500' : 'border-transparent'}`}
            style={activeTab !== tab.key ? { color:'var(--text-secondary)' } : {}}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="card">
          <h3 className="font-semibold text-sm mb-3" style={{ color:'var(--text)' }}>Bug Info</h3>
          <InfoRow label="Project">{bug.project_name ? <Link to={`/projects/${bug.project_id}`} className="text-brand-500 hover:underline">{bug.project_name}</Link> : '—'}</InfoRow>
          {bug.task_title && <InfoRow label="Related Task"><Link to={`/tasks/${bug.task_id}`} className="text-brand-500 hover:underline">{bug.task_title}</Link></InfoRow>}
          <InfoRow label="Assigned To">{bug.assigned_to_name ? <div className="flex items-center gap-2"><div className="w-5 h-5 rounded text-xs font-bold text-white flex items-center justify-center" style={{ background:'#234180' }}>{bug.assigned_to_name[0]}</div>{bug.assigned_to_name}</div> : 'Unassigned'}</InfoRow>
          <InfoRow label="Reported By" value={bug.created_by_name} />
          <InfoRow label="Reported"    value={fmt(bug.created_at,'MMM d, yyyy')} />
          <InfoRow label="Updated"     value={fmt(bug.updated_at,'MMM d, yyyy')} />
          {bug.resolved_at && <InfoRow label="Resolved" value={fmt(bug.resolved_at,'MMM d, yyyy')} />}
        </div>

        <div className="lg:col-span-2 space-y-5">
          {bug.description && (
            <div className="card">
              <h3 className="font-semibold text-sm mb-3" style={{ color:'var(--text)' }}>Description</h3>
              <p className="text-sm leading-relaxed" style={{ color:'var(--text)' }}>{bug.description}</p>
            </div>
          )}
          {bug.steps_to_reproduce && (
            <div className="card">
              <h3 className="font-semibold text-sm mb-3" style={{ color:'var(--text)' }}>Steps to Reproduce</h3>
              <pre className="text-sm font-mono whitespace-pre-wrap leading-relaxed p-4 rounded-xl" style={{ background:'var(--surface-alt)', color:'var(--text)' }}>{bug.steps_to_reproduce}</pre>
            </div>
          )}
          {bug.attachments?.length > 0 && (
            <div className="card">
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ color:'var(--text)' }}><ClipIc /> Attachments ({bug.attachments.length})</h3>
              <div className="space-y-2">
                {bug.attachments.map((att,i) => (
                  <a key={i} href={`/uploads/${att.filename}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 rounded-xl hover:bg-[var(--surface-alt)] transition-colors text-brand-500 text-sm">
                    <ClipIc /> <span className="truncate">{att.original_name}</span>
                    {att.file_size && <span className="ml-auto text-xs flex-shrink-0" style={{ color:'var(--text-secondary)' }}>{(att.file_size/1024).toFixed(1)} KB</span>}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>}

      {/* QA Pipeline Tab */}
      {activeTab === 'pipeline' && (
        <div className="card">
          <h3 className="font-semibold text-sm mb-4" style={{ color:'var(--text)' }}>QA Pipeline Progress</h3>
          <div className="space-y-3">
            {[
              { label:'Fixed in Development', date: bug.fixed_in_dev_date },
              { label:'Deployed to UAT',       date: bug.deployed_uat_date },
              { label:'Deployed to Production',date: bug.deployed_prod_date },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-3 p-3 rounded-xl" style={{ background:s.date?'#ecfdf5':'var(--surface-alt)', border:`1px solid ${s.date?'#6ee7b7':'var(--border)'}` }}>
                <span className="text-lg">{s.date ? 'v' : 'o'}</span>
                <div>
                  <p className="text-sm font-semibold" style={{ color:'var(--text)' }}>{s.label}</p>
                  {s.date && <p className="text-xs" style={{ color:'#059669' }}>{s.date}</p>}
                  {!s.date && <p className="text-xs" style={{ color:'var(--text-secondary)' }}>Pending</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attachments Tab */}
      {activeTab === 'attachments' && (
        <div className="card">
          <h3 className="font-semibold text-sm mb-4" style={{ color:'var(--text)' }}>Attachments</h3>
          <div className="space-y-2">
            {(bug.attachments||[]).map(a => (
              <div key={a.id} className="flex items-center gap-3 p-3 rounded-xl border" style={{ borderColor:'var(--border)' }}>
                <span className="text-lg">📎</span>
                <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-sm text-brand-500 hover:underline flex-1">{a.filename}</a>
              </div>
            ))}
            {(!bug.attachments || bug.attachments.length === 0) && (
              <p className="text-sm text-center py-8" style={{ color:'var(--text-secondary)' }}>No attachments</p>
            )}
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <Modal isOpen={editOpen} onClose={() => setEditOpen(false)} title="Edit Bug" size="lg">
        <div className="space-y-4">
          {/* Shared with BugsPage and DevHubPage — edit BugFormFields.jsx. */}
          <BugFormFields form={editForm} setForm={setEditForm}
            projects={projects} users={usersList} me={me} showStatus />
          {/* Fields an administrator defined under Admin, Custom Fields for
              Bug Reports. The column and the API have accepted these since
              phase50; this page simply never rendered them. */}
          <CustomFieldsEditor appliesTo="bug"
            values={editForm.custom_fields || {}}
            onChange={v => f({ custom_fields: v })} />
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setEditOpen(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleSave} disabled={saving||!bugFormValid(editForm)} className="btn-primary flex items-center gap-2">
                {saving && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
                Save Changes
              </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog isOpen={deleteOpen} onClose={() => setDeleteOpen(false)} onConfirm={handleDelete}
        title="Delete Bug" message={`Delete "${bug.title}" permanently?`} />
    </div>
  );
}
